// Offline Action Guard Component
import React from 'react';
import { useOfflineMode } from '../../hooks/useOfflineMode.js';
import { useNotifications } from '../notifications/NotificationProvider.jsx';
import { getOfflineMessage, getOfflineDisabledClasses } from '../../utils/offlineUtils.js';

/**
 * Component that wraps buttons/actions to disable them when offline
 * Shows appropriate messaging and prevents actions when in read-only mode
 */
export function OfflineActionGuard({
  children,
  featureType = 'modify',
  fallbackMessage = null,
  showTooltip = true,
  className = '',
  ...props
}) {
  const { isOffline, readOnlyMode } = useOfflineMode();
  const { showError } = useNotifications();

  const isDisabled =
    readOnlyMode &&
    ['create', 'edit', 'delete', 'schedule', 'import', 'assign', 'modify'].includes(featureType);
  const disabledClasses = getOfflineDisabledClasses(isDisabled);
  const message = fallbackMessage || getOfflineMessage(featureType);

  const handleClick = (originalOnClick) => (event) => {
    if (isDisabled) {
      event.preventDefault();
      event.stopPropagation();
      showError(message);
      return;
    }

    if (originalOnClick) {
      originalOnClick(event);
    }
  };

  // Clone children and modify their props
  const enhancedChildren = React.Children.map(children, (child) => {
    if (!React.isValidElement(child)) {
      return child;
    }

    // Handle different element types
    if (child.type === 'button' || child.props.onClick) {
      return React.cloneElement(child, {
        ...child.props,
        disabled: child.props.disabled || isDisabled,
        className: `${child.props.className || ''} ${disabledClasses}`.trim(),
        onClick: handleClick(child.props.onClick),
        title: isDisabled && showTooltip ? message : child.props.title,
        'aria-disabled': isDisabled,
        'data-offline-disabled': isDisabled,
      });
    }

    return child;
  });

  return (
    <div className={`${className} ${isDisabled ? 'relative' : ''}`} {...props}>
      {enhancedChildren}
      {isDisabled && showTooltip && (
        <div className="absolute inset-0 cursor-not-allowed" title={message} />
      )}
    </div>
  );
}

/**
 * Higher-order component that wraps a component with offline protection
 */
export function withOfflineGuard(WrappedComponent, featureType = 'modify') {
  return function OfflineGuardedComponent(props) {
    return (
      <OfflineActionGuard featureType={featureType}>
        <WrappedComponent {...props} />
      </OfflineActionGuard>
    );
  };
}
